// src/scenes/sitemanager/DashboardMPLH.jsx

import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    TextField,
    Card,
    CardContent,
    Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
} from "recharts";
import StatBox from "../../component-files/StatBox";
import Header from "../../component-files/Header";
import axios from "axios";
import { tokens } from "../../theme";
import { useAuth } from "../../context/AuthContext";

// helper to extract YYYY-MM
const monthKey = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function DashboardMPLH() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { allocations, isSchoolUser } = useAuth();

    // compute current school year range: July 1 – June 30
    const now = new Date();
    const year = now.getFullYear();
    const syStartYear = now.getMonth() + 1 >= 7 ? year : year - 1;
    const syStart = new Date(syStartYear, 6, 1);      // July 1
    const syEnd = new Date(syStartYear + 1, 5, 30, 23, 59, 59); // June 30

    // filters
    const [siteFilter, setSiteFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");

    // containers
    const [sites, setSites] = useState([]);
    const [months, setMonths] = useState([]);
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState({ totalMEQ: 0, totalHours: 0 });
    const [districtData, setDistrictData] = useState([]);
    const [siteMplhData, setSiteMplhData] = useState([]);
    const [leaderBoardData, setLeaderBoardData] = useState([]);

    // MEQ conversion
    const [conv, setConv] = useState({
        lunch: 1.0,
        supper: 1.0,
        breakfast: 0.67,
        snack: 0.33,
    });

    // 1) Load filter dropdowns (sites + months) within school year
    useEffect(() => {
        (async () => {
            const [mealsRes, laborsRes] = await Promise.all([
                axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts"),
                axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_LaborHours"),
            ]);
            const mealsAll = mealsRes.data.filter(m => {
                const dt = new Date(m.Date);
                return dt >= syStart && dt <= syEnd;
            });
            const laborsAll = laborsRes.data.filter(l => {
                const dt = new Date(l.Date);
                return dt >= syStart && dt <= syEnd;
            });

            // build sites list limited to manager
            const siteMap = {};
            mealsAll.forEach(m => {
                if (!isSchoolUser || allocations.some(a => a.SchoolId === m.SchoolId)) {
                    siteMap[m.SchoolId] = m.SchoolName;
                }
            });
            laborsAll.forEach(l => {
                if (!isSchoolUser || allocations.some(a => a.SchoolId === l.SchoolId)) {
                    siteMap[l.SchoolId] = l.SchoolName;
                }
            });
            const siteOptions = Object.entries(siteMap).map(([id, name]) => ({ id, name }));
            setSites(siteOptions);
            if (siteOptions.length && !siteFilter) {
                setSiteFilter(siteOptions[0].id);
            }

            // build months list within school year
            const monthSet = new Set();
            mealsAll.forEach(m => monthSet.add(monthKey(m.Date)));
            laborsAll.forEach(l => monthSet.add(monthKey(l.Date)));
            setMonths(Array.from(monthSet).sort());
        })();
    }, [allocations, isSchoolUser]);

    // 2) Compute monthly detail grid & summary stat within school year
    useEffect(() => {
        (async () => {
            const [mealsRes, laborsRes] = await Promise.all([
                axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts"),
                axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_LaborHours"),
            ]);
            let meals = mealsRes.data.filter(m => {
                const dt = new Date(m.Date);
                return dt >= syStart && dt <= syEnd &&
                    (!isSchoolUser || allocations.some(a => a.SchoolId === m.SchoolId));
            });
            let labors = laborsRes.data.filter(l => {
                const dt = new Date(l.Date);
                return dt >= syStart && dt <= syEnd &&
                    (!isSchoolUser || allocations.some(a => a.SchoolId === l.SchoolId));
            });

            if (siteFilter) meals = meals.filter(m => m.SchoolId === siteFilter);
            if (monthFilter) meals = meals.filter(m => monthKey(m.Date) === monthFilter);
            if (siteFilter) labors = labors.filter(l => l.SchoolId === siteFilter);
            if (monthFilter) labors = labors.filter(l => monthKey(l.Date) === monthFilter);

            const mealGroups = meals.reduce((acc, m) => {
                (acc[monthKey(m.Date)] ||= []).push(m);
                return acc;
            }, {});
            const laborGroups = labors.reduce((acc, l) => {
                (acc[monthKey(l.Date)] ||= []).push(l);
                return acc;
            }, {});
            const allMonths = Array.from(
                new Set([...Object.keys(mealGroups), ...Object.keys(laborGroups)])
            ).sort();

            const detailRows = allMonths.map(mon => {
                const mRecs = mealGroups[mon] || [];
                const lRecs = laborGroups[mon] || [];
                const bCnt = mRecs.reduce((s, m) => s + (m.BR_Free || 0) + (m.BR_Paid || 0) + (m.BR_Reduced || 0), 0);
                const lCnt = mRecs.reduce((s, m) => s + (m.LN_Free || 0) + (m.LN_Paid || 0) + (m.LN_Reduced || 0), 0);
                const sCnt = mRecs.reduce((s, m) => s + (m.SN_Free || 0), 0);
                const spCnt = mRecs.reduce((s, m) => s + (m.SP_Free || 0) + (m.SP_Paid || 0) + (m.SP_Reduced || 0), 0);

                const totalMEQ = bCnt * conv.breakfast + lCnt * conv.lunch + sCnt * conv.snack + spCnt * conv.supper;
                const totalHrs = lRecs.reduce((s, l) => s + (l.LaborHours || 0), 0);
                const mplh = totalHrs > 0 ? +(totalMEQ / totalHrs).toFixed(2) : null;

                return {
                    id: mon,
                    month: mon,
                    breakfastCount: bCnt,
                    lunchCount: lCnt,
                    snackCount: sCnt,
                    supperCount: spCnt,
                    totalHours: totalHrs,
                    totalMEQ,
                    mplh,
                };
            });

            setRows(detailRows);
            setStats({
                totalMEQ: detailRows.reduce((s, r) => s + r.totalMEQ, 0),
                totalHours: detailRows.reduce((s, r) => s + r.totalHours, 0),
            });
        })();
    }, [siteFilter, monthFilter, conv, allocations, isSchoolUser]);

    // 3) Compute MPLH per site & leaderboard within school year
    useEffect(() => {
        (async () => {
            const [mealsRes, laborsRes] = await Promise.all([
                axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts"),
                axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_LaborHours"),
            ]);
            const mealsYear = mealsRes.data.filter(m => {
                const dt = new Date(m.Date);
                return dt >= syStart && dt <= syEnd;
            });
            const laborsYear = laborsRes.data.filter(l => {
                const dt = new Date(l.Date);
                return dt >= syStart && dt <= syEnd;
            });

            // group by school
            const map = {};
            mealsYear.forEach(m => {
                if (!isSchoolUser || allocations.some(a => a.SchoolId === m.SchoolId)) {
                    (map[m.SchoolId] ||= { siteId: m.SchoolId, name: m.SchoolName, meals: [], labors: [] })
                        .meals.push(m);
                }
            });
            laborsYear.forEach(l => {
                if (!isSchoolUser || allocations.some(a => a.SchoolId === l.SchoolId)) {
                    (map[l.SchoolId] ||= { siteId: l.SchoolId, name: l.SchoolName, meals: [], labors: [] })
                        .labors.push(l);
                }
            });

            const allSitesData = Object.values(map)
                .map(g => {
                    const b = g.meals.reduce((s, m) => s + (m.BR_Free || 0) + (m.BR_Paid || 0) + (m.BR_Reduced || 0), 0);
                    const l = g.meals.reduce((s, m) => s + (m.LN_Free || 0) + (m.LN_Paid || 0) + (m.LN_Reduced || 0), 0);
                    const s = g.meals.reduce((s, m) => s + (m.SN_Free || 0), 0);
                    const sp = g.meals.reduce((s, m) => s + (m.SP_Free || 0) + (m.SP_Paid || 0) + (m.SP_Reduced || 0), 0);
                    const totalMEQ = b * conv.breakfast + l * conv.lunch + s * conv.snack + sp * conv.supper;
                    const hrs = g.labors.reduce((s, l) => s + (l.LaborHours || 0), 0);
                    return { siteId: g.siteId, siteName: g.name, mplh: hrs > 0 ? +(totalMEQ / hrs).toFixed(2) : 0 };
                })
                .sort((a, b) => b.mplh - a.mplh);

            setDistrictData(allSitesData);

            const managerSites = isSchoolUser
                ? allSitesData.filter(d => allocations.some(a => a.SchoolId === d.siteId))
                : allSitesData;
            setSiteMplhData(managerSites);

            setLeaderBoardData(allSitesData.slice(0, 10));
        })();
    }, [conv, allocations, isSchoolUser]);

    // current rank
    const currentRank = (() => {
        if (!siteFilter) return null;
        const idx = districtData.findIndex(d => d.siteId === siteFilter);
        return idx >= 0 ? idx + 1 : null;
    })();
    const totalSites = districtData.length;

    const columns = [
        { field: "month", headerName: "Month", width: 120 },
        { field: "breakfastCount", headerName: "Breakfast", type: "number", width: 120 },
        { field: "lunchCount", headerName: "Lunch", type: "number", width: 90 },
        { field: "snackCount", headerName: "Snack", type: "number", width: 90 },
        { field: "supperCount", headerName: "Supper", type: "number", width: 90 },
        { field: "totalHours", headerName: "Labor Hrs", type: "number", width: 100 },
        {
            field: "totalMEQ", headerName: "MEQs", type: "number", width: 100,
            valueFormatter: ({ value }) => value.toFixed(2)
        },
        {
            field: "mplh", headerName: "MPLH", type: "number", width: 100,
            valueFormatter: ({ value }) => value != null ? value.toFixed(2) : "—"
        },
    ];

    return (
        <Box p={3}>
            <Header title="Site MPLH Dashboard" subtitle="Your assigned sites only" />

            {/* Conversion inputs */}
            <Box display="flex" gap={2} mb={3}>
                {["lunch", "supper", "breakfast", "snack"].map(key => (
                    <TextField
                        key={key}
                        label={`${key.charAt(0).toUpperCase() + key.slice(1)} MEQ`}
                        type="number" size="small"
                        inputProps={{ step: 0.01, min: 0 }}
                        value={conv[key]}
                        onChange={e => setConv(c => ({ ...c, [key]: parseFloat(e.target.value) || 0 }))}
                    />
                ))}
            </Box>

            {/* Filters */}
            <Box display="flex" gap={2} mb={3}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Site</InputLabel>
                    <Select value={siteFilter} label="Site" onChange={e => setSiteFilter(e.target.value)}>
                        {sites.map(s => (
                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Month</InputLabel>
                    <Select value={monthFilter} label="Month" onChange={e => setMonthFilter(e.target.value)}>
                        <MenuItem value="">All Months</MenuItem>
                        {months.map(m => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* MPLH stat */}
            <StatBox
                title={stats.totalHours > 0 ? (stats.totalMEQ / stats.totalHours).toFixed(2) : "—"}
                subtitle={`${stats.totalMEQ.toFixed(2)} MEQs ÷ ${stats.totalHours.toFixed(2)} hrs`}
                borderColor={colors.primary[500]}
            />

            {/* Charts + Rank + Leaderboard */}
            <Box display="flex" gap={2} mb={3} alignItems="flex-start">
                <Box flex={2}>
                    <Typography variant="h6" gutterBottom>
                        Your Sites MPLH ({syStartYear}/{syStartYear + 1})
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={siteMplhData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="siteName" angle={-45} textAnchor="end" interval={0} />
                            <YAxis />
                            <RechartsTooltip />
                            <Bar dataKey="mplh" fill={colors.blueAccent[500]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
                {currentRank && (
                    <Box flex={1} sx={{
                        p: 4, borderRadius: 3, textAlign: "center",
                        boxShadow: `0 0 20px ${colors.greenAccent[500]}`, backgroundColor: "transparent"
                    }}>
                        <Typography variant="h6" gutterBottom>
                            {sites.find(s => s.id === siteFilter)?.name}
                        </Typography>
                        <Typography variant="h1" fontWeight="bold" mb={1}>
                            {currentRank}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            rank out of {totalSites}
                        </Typography>
                        <Typography variant="h2" fontWeight="bold" mb={1}>
                            {stats.totalHours > 0 ? (stats.totalMEQ / stats.totalHours).toFixed(2) : "—"}
                        </Typography>
                        <Typography variant="subtitle2" color={colors.greenAccent[700]} gutterBottom>
                            MEQ per Labor Hour
                        </Typography>
                        <Typography variant="body2" color={colors.greenAccent[700]}>
                            Keep up the great work!
                        </Typography>
                    </Box>
                )}
                <Box flex={1}>
                    <Typography variant="h6" gutterBottom>
                        🏆 Top 10 Sites MPLH (District)
                    </Typography>
                    <Box component="ol" sx={{
                        pl: 2, m: 0,
                        "& li": { mb: 0.5, borderRadius: 1, px: 1, py: 0.5, display: "flex", justifyContent: "space-between" },
                        "& li:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover }
                    }}>
                        {leaderBoardData.map(({ siteName, mplh }, i) => (
                            <Typography key={siteName} component="li" sx={{
                                fontWeight: siteName === sites.find(s => s.id === siteFilter)?.name ? "bold" : "inherit"
                            }}>
                                <span>{`${i + 1}. ${siteName}`}</span>
                                <span>{mplh}</span>
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Detail grid */}
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">Monthly Detail</Typography>
                        <Button variant="contained" onClick={() => {
                            const header = ["Site", "Month", "Breakfast", "Lunch", "Snack", "Supper", "LaborHours", "MEQs", "MPLH"];
                            const csv = [
                                header.join(","),
                                ...rows.map(r => [
                                    sites.find(s => s.id === siteFilter)?.name || "",
                                    r.month,
                                    r.breakfastCount,
                                    r.lunchCount,
                                    r.snackCount,
                                    r.supperCount,
                                    r.totalHours,
                                    r.totalMEQ.toFixed(2),
                                    r.mplh != null ? r.mplh.toFixed(2) : ""
                                ].join(","))
                            ].join("\n");
                            const blob = new Blob([csv], { type: "text/csv" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = "meals_per_labor_hour.csv";
                            a.click();
                            URL.revokeObjectURL(url);
                        }} sx={{
                            backgroundColor: theme.palette.success.main,
                            color: theme.palette.getContrastText(theme.palette.success.main),
                            "&:hover": { backgroundColor: theme.palette.success.dark }
                        }}>
                            Download CSV
                        </Button>
                    </Box>
                    <Box sx={{ height: 500, width: "100%" }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            pagination
                            disableSelectionOnClick
                            sx={{
                                "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
                                "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "transparent" }
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
